
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import IncludeUnappliedToggle from '@/components/IncludeUnappliedToggle.vue';
import settingx from '@/store/modules/settingx';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		IncludeUnappliedToggle,
	},
})
export default class SupplierAgingSummaryBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['suppliers', 'agents', 'dateAsOf'];

	public result: number[] = [];
	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get includeUnapplied() {
		return settingx.now.includeUnapplied;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedAgents, selectedSuppliers, includeUnapplied } = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedSuppliers,
			includeUnapplied,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const asOf = this.selectedAsOfDate;
		const msPerDay = 24 * 60 * 60 * 1000;

		// Build aging bucket CASE WHEN expressions
		const buildAgingBuckets = (dateCol: string, amountCol: string) => {
			const daysAgo = `((${asOf} - TRY_CAST(${dateCol} AS BIGINT)) / ${msPerDay})`;
			const amt = `TRY_CAST(${amountCol} AS DOUBLE)`;
			return `
				SUM(CASE WHEN ${daysAgo} >= 0 AND ${daysAgo} < 30 THEN ${amt} ELSE 0 END) as b0,
				SUM(CASE WHEN ${daysAgo} >= 30 AND ${daysAgo} < 60 THEN ${amt} ELSE 0 END) as b30,
				SUM(CASE WHEN ${daysAgo} >= 60 AND ${daysAgo} < 90 THEN ${amt} ELSE 0 END) as b60,
				SUM(CASE WHEN ${daysAgo} >= 90 AND ${daysAgo} < 120 THEN ${amt} ELSE 0 END) as b90,
				SUM(CASE WHEN ${daysAgo} >= 120 THEN ${amt} ELSE 0 END) as b120
			`;
		};

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let supplierFilter = '';
		if (this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((c) => `'${escapeStr(c)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const pisdTable = getTableName('supplierPiSds');
		const koTable = getTableName('supplierKos');
		const tradeTable = getTableName('supplierTrades');

		const queryHead = await this._ensureTablesLoaded(['supplierPiSds', 'supplierKos', 'supplierTrades']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Run all queries in parallel - group by supplier code for FIFO
		const [pisdData, koData, gainLossData, unappliedData] = await Promise.all([
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					${buildAgingBuckets('"DOCDATE"', '"LOCALDOCAMT"')}
				FROM ${pisdTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOf}
					${supplierFilter}${agentFilter}
				GROUP BY "CODE"
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					${buildAgingBuckets('"TODOCDATE"', '"KOLOCALAMT"')}
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOf}
					AND TRY_CAST("FROMDOCDATE" AS BIGINT) <= ${asOf}
					${supplierFilter}${agentFilter}
				GROUP BY "CODE"
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					${buildAgingBuckets('"TODOCDATE"', '"KOGAINLOSS"')}
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOf}
					AND TRY_CAST("FROMDOCDATE" AS BIGINT) <= ${asOf}
					${supplierFilter}${agentFilter}
				GROUP BY "CODE"
			`),
			queryHead.rawQuery(`
				SELECT "CODE" as code,
					SUM(TRY_CAST("UNAPPLIEDAMT" AS DOUBLE)) as unapplied
				FROM ${tradeTable}
				WHERE TRY_CAST("DOCDATE" AS BIGINT) <= ${asOf}
					${supplierFilter}${agentFilter}
				GROUP BY "CODE"
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.95);

		// Build lookup maps by supplier code
		const koMap = new Map<string, number[]>();
		for (const row of koData) {
			koMap.set(String(row.code), [
				Number(row.b0) || 0, Number(row.b30) || 0,
				Number(row.b60) || 0, Number(row.b90) || 0,
				Number(row.b120) || 0,
			]);
		}

		const gainLossMap = new Map<string, number[]>();
		for (const row of gainLossData) {
			gainLossMap.set(String(row.code), [
				Number(row.b0) || 0, Number(row.b30) || 0,
				Number(row.b60) || 0, Number(row.b90) || 0,
				Number(row.b120) || 0,
			]);
		}

		const unappliedMap = new Map<string, number>();
		for (const row of unappliedData) {
			unappliedMap.set(String(row.code), Number(row.unapplied) || 0);
		}

		// Process each supplier: pisd - ko - gainLoss, then FIFO
		const periodTotals = [0, 0, 0, 0, 0];
		const periodLabels = [
			'Current', '> 30 Days', '> 60 Days', '> 90 Days', '> 120 Days',
		];

		for (const row of pisdData) {
			const code = String(row.code);
			const pisdBuckets = [
				Number(row.b0) || 0, Number(row.b30) || 0,
				Number(row.b60) || 0, Number(row.b90) || 0,
				Number(row.b120) || 0,
			];
			const koBuckets = koMap.get(code) || [0, 0, 0, 0, 0];
			const glBuckets = gainLossMap.get(code) || [0, 0, 0, 0, 0];

			// Net amounts per bucket
			const netBuckets = pisdBuckets.map(
				(v, i) => v - koBuckets[i] - glBuckets[i],
			);

			// Apply FIFO for unapplied (from oldest to newest)
			if (this.includeUnapplied) {
				let remaining = unappliedMap.get(code) || 0;
				for (let i = 4; i >= 0 && remaining > 0; i--) {
					const deduction = Math.min(netBuckets[i], remaining);
					netBuckets[i] -= deduction;
					remaining -= deduction;
				}
			}

			// Add to totals
			for (let i = 0; i < 5; i++) {
				periodTotals[i] += netBuckets[i];
			}
		}

		this.xAxisData = periodLabels;
		this.result = periodTotals;

		this.series = [
			{
				name: 'Total',
				data: this.result,
				type: 'bar',
				barWidth: '40%',
			},
		];

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData');
	}
}
